/**
 * Clean and format price by removing HTML tags and entities.
 *
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';

import { getCurrencyConfig } from '../get-currency-config';
import type { HtmlEntityMap, PriceFormatOptions } from '../types';

/**
 * HTML entity mapping for price formatting.
 *
 * @since 1.0.0
 */
const HTML_ENTITIES: HtmlEntityMap = {
  '&nbsp;': ' ',
  '&#2547;': '৳', // Bengali Taka symbol
  '&#8377;': '₹', // Indian Rupee symbol
  '&#8364;': '€', // Euro symbol
  '&#163;': '£', // Pound symbol
  '&#165;': '¥', // Yen symbol
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#039;': "'",
} as const;

/**
 * Clean and format price by removing HTML tags and entities.
 *
 * @since 1.0.0
 *
 * @param priceString - The price string or number to format.
 * @param options - Optional formatting options.
 * @return Formatted price string.
 */
export const formatPrice = (priceString: string | number, options?: PriceFormatOptions): string => {
  try {
    const config = { ...getCurrencyConfig(), ...options };

    if (typeof priceString === 'number') {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency,
        minimumFractionDigits: config.minimumFractionDigits,
        maximumFractionDigits: config.maximumFractionDigits,
      }).format(priceString);
    }
  } catch {
    return String(priceString);
  }

  try {
    const config = { ...getCurrencyConfig(), ...options };

    // If it's a string, remove HTML tags and decode entities
    let cleanPrice = priceString.toString();

    // Remove HTML tags
    cleanPrice = cleanPrice.replace(/<[^>]*>/g, '');

    // Decode HTML entities
    Object.entries(HTML_ENTITIES).forEach(([entity, replacement]) => {
      cleanPrice = cleanPrice.replace(new RegExp(entity, 'g'), replacement);
    });

    // Use temporary div for any remaining entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanPrice;
    cleanPrice = tempDiv.textContent ?? tempDiv.innerText ?? cleanPrice;

    // Clean up extra whitespace
    cleanPrice = cleanPrice.replace(/\s+/g, ' ').trim();

    // If the result is empty or just contains numbers, fallback to numeric formatting
    if (!cleanPrice || /^[\d.,]+$/.test(cleanPrice)) {
      const numericValue = parseFloat(cleanPrice || '0');
      if (!isNaN(numericValue)) {
        return new Intl.NumberFormat(config.locale, {
          style: 'currency',
          currency: config.currency,
          minimumFractionDigits: config.minimumFractionDigits,
          maximumFractionDigits: config.maximumFractionDigits,
        }).format(numericValue);
      }
    }

    return cleanPrice;
  } catch {
    return String(priceString);
  }
};
